// src/components/register/RegistrationWizard/Steps/AccountCreationStep.js
import React, { useState, useEffect, useRef} from "react";
import { useRegistration } from "../../../../context/RegistrationContext";
import { toast } from "react-toastify";
import OpguruLogo from "../../../../assets/opgurulogo.png";
import { TailSpin } from "react-loader-spinner";
import {
  faCheck,
  faTimes,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "../../../../api/axios";

// Password regex - at least one uppercase, lowercase, number, special character
const PWD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%+-]).{8,24}$/;
const REGISTER_URL = "/api/register";
const AUTH_URL = "/api/auth";

const AccountCreationStep = () => {
  const {
    generatedUserId,
    password,
    confirmPassword,
    setField,
    nextStep,
    prevStep,
    validateStep,
    formErrors,
    clearFormError,
    name,
    email,
    phno,
  } = useRegistration();

  const [loading, setLoading] = useState(false);
  const [validPwd, setValidPwd] = useState(false);
  const [pwdFocus, setPwdFocus] = useState(false);
  const [validMatch, setValidMatch] = useState(false);
  const [matchFocus, setMatchFocus] = useState(false);

  const prevPasswordRef = useRef(password);
  const prevConfirmPasswordRef = useRef(confirmPassword);

  // Validate password as user types
  useEffect(() => {
    const isValidPassword = PWD_REGEX.test(password);
    setValidPwd(isValidPassword);

    // Clear password error if password becomes valid
    if (isValidPassword && prevPasswordRef.current !== password) {
      clearFormError("password");
    }

    // Check if passwords match
    const doPasswordsMatch = password === confirmPassword;
    setValidMatch(doPasswordsMatch);

    // Clear confirmPassword error if passwords match
    if (doPasswordsMatch && (prevPasswordRef.current !== password || prevConfirmPasswordRef.current !== confirmPassword)) {
      clearFormError("confirmPassword");
    }
  
    prevPasswordRef.current = password;
    prevConfirmPasswordRef.current = confirmPassword;
  }, [password, confirmPassword, clearFormError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!validateStep(2)) {
        toast.error("Please ensure your password meets the requirements", {
            position: "top-center",
        });
        setLoading(false);
        return;
    }

    try {
        // Step 1: Register the user
        const userData = {
            name,
            email,
            userID: generatedUserId,
            phno,
            password,
            loginroute: "usertype",
        };

        const registerResponse = await axios.post(REGISTER_URL, userData);
        if (!registerResponse.data) {
            throw new Error("Failed to save account details");
        }

        // Step 2: Authenticate to get access token
        const loginResponse = await axios.post(AUTH_URL, {
            phno,
            password,
        });

        const access_token = loginResponse.data.accessToken;
        if (!access_token) {
            throw new Error("No access token received from server. Please contact support.");
        }

        setField("access_token", access_token);
        setField("password", ""); // Clear password after use
        setField("confirmPassword", "");

        toast.success("Account created successfully! Please complete your profile.", {
            position: "top-center",
        });

        nextStep();
    } catch (err) {
        console.error("Account creation error:", err);
        const errorMessage = err.response?.data?.message || "Failed to create account. Please try again.";
        toast.error(errorMessage, { position: "top-center" });

        if (err.response?.status === 500) {
            toast.error("Server error occurred. Please try again later.", { position: "top-center" });
        } else if (err.response?.status === 400 && err.response.data.message.includes("already exists")) {
            toast.error("This phone number or email is already registered.", { position: "top-center" });
        }
    } finally {
        setLoading(false);
    }
};

  return (
    <div className="step-container">
      <div className="auth-header">
        <div className="auth-title">Create Account</div>
        <div className="auth-logo">
          <img src={OpguruLogo} alt="OpGuru Logo" />
        </div>
      </div>

      <div className="welcome-message">
        Your User ID: <strong>{generatedUserId}</strong>
      </div>

      <form onSubmit={handleSubmit} className="registration-form">
        <div className="input-group password-group">
          <div className="password-input-container">
            <input
              type="password"
              id="password"
              placeholder="Create Password"
              autoComplete="new-password"
              onChange={(e) => setField("password", e.target.value)}
              value={password}
              required
              aria-invalid={validPwd ? "false" : "true"}
              aria-describedby="pwdnote"
              onFocus={() => setPwdFocus(true)}
              onBlur={() => setPwdFocus(false)}
              className={formErrors.password ? "error-input" : ""}
            />
            <FontAwesomeIcon
              icon={faCheck}
              className={validPwd ? "valid-icon" : "hidden-icon"}
            />
            <FontAwesomeIcon
              icon={faTimes}
              className={validPwd || !password ? "hidden-icon" : "invalid-icon"}
            />
          </div>

          {formErrors.password && (
            <div className="error-message">{formErrors.password}</div>
          )}

          <p
            id="pwdnote"
            className={pwdFocus && !validPwd ? "instructions" : "offscreen"}
          >
            <FontAwesomeIcon icon={faInfoCircle} />
            8 to 24 characters
            <br />
            Must include uppercase and lowercase letters, a number and a special
            character
            <br />
            Allowed special characters:{" "}
            <span aria-label="exclamation mark">!</span>{" "}
            <span aria-label="at symbol">@</span>{" "}
            <span aria-label="hashtag">#</span>{" "}
            <span aria-label="dollar sign">$</span>{" "}
            <span aria-label="percent">%</span> <span aria-label="plus">+</span>
          </p>
        </div>

        <div className="input-group password-group">
          <div className="password-input-container">
            <input
              type="password"
              id="confirm-password"
              placeholder="Confirm Password"
              autoComplete="new-password"
              onChange={(e) => setField("confirmPassword", e.target.value)}
              value={confirmPassword}
              required
              aria-invalid={validMatch ? "false" : "true"}
              aria-describedby="confirmnote"
              onFocus={() => setMatchFocus(true)}
              onBlur={() => setMatchFocus(false)}
              className={formErrors.confirmPassword ? "error-input" : ""}
            />
            <FontAwesomeIcon
              icon={faCheck}
              className={
                validMatch && confirmPassword ? "valid-icon" : "hidden-icon"
              }
            />
            <FontAwesomeIcon
              icon={faTimes}
              className={
                validMatch || !confirmPassword ? "hidden-icon" : "invalid-icon"
              }
            />
          </div>

          {formErrors.confirmPassword && (
            <div className="error-message">{formErrors.confirmPassword}</div>
          )}

          <p
            id="confirmnote"
            className={matchFocus && !validMatch ? "instructions" : "offscreen"}
            style={{ color: "black" }}
          >
            <FontAwesomeIcon icon={faInfoCircle} />
            Must match the password field.
          </p>
        </div>

        <div className="terms-conditions">
          By creating an account, you are agreeing to our{" "}
          <span className="terms-link">Terms And Conditions.</span>
        </div>

        <div className="button-group">
          <button type="button" className="secondary-button" onClick={prevStep}>
            Back
          </button>

          <button
            type="submit"
            className="primary-button"
            disabled={loading || !validPwd || !validMatch}
          >
            {loading ? (
              <TailSpin
                visible={true}
                height="24"
                width="24"
                color="#ffffff"
                ariaLabel="tail-spin-loading"
                radius="1"
              />
            ) : (
              "Next"
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AccountCreationStep;
